import { DestroyRef, EventEmitter, inject } from '@angular/core';
import { ModelFormService, SubmitShortcut } from '@common/forms/frontend-shared';
import { takeWhileAlive } from '@core/frontend-shared/utils';
import { getItemModelText } from '@core/shared/model';
import { isConstructor } from '@core/shared/utils';
import { BehaviorSubject, filter, map, startWith, switchMap } from 'rxjs';
import { CrudItemViewComponent } from './crud-item-view.component';
import { AsyncPipe } from '@angular/common';
import { UiLoadingIndicatorComponent } from '@common/ui/frontend-shared/loading-indicator';
import { CrudFieldsetComponent } from '../crud-fieldset/crud-fieldset.component';
import { CrudItemToolbarComponent } from './crud-item-toolbar.component';
import * as i0 from "@angular/core";
import * as i1 from "@common/ui/frontend-shared/panel";
const _c0 = [[["ui-header"]], [["ui-footer"]]];
const _c1 = ["ui-header", "ui-footer"];
function CrudEditorComponent_Conditional_1_Conditional_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 3)(1, "div", 4);
    i0.ɵɵtext(2);
    i0.ɵɵelementEnd();
    i0.ɵɵelement(3, "div", 5);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    i0.ɵɵadvance(2);
    i0.ɵɵtextInterpolate(ctx);
  }
}
function CrudEditorComponent_Conditional_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "ui-header");
    i0.ɵɵconditionalCreate(1, CrudEditorComponent_Conditional_1_Conditional_1_Template, 4, 1, "div", 3);
    i0.ɵɵpipe(2, "async");
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    let tmp_1_0;
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance();
    i0.ɵɵconditional((tmp_1_0 = i0.ɵɵpipeBind1(2, 1, ctx_r0.title$)) ? 1 : -1, tmp_1_0);
  }
}
function CrudEditorComponent_Conditional_5_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "crud-fieldset", 2);
    i0.ɵɵpipe(1, "async");
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵproperty("instructions", ctx_r0.innerFormComponent ? null : ctx_r0.instructions)("crudItemViewService", ctx_r0.crudItemViewService)("formService", ctx)("innerFormComponent", ctx_r0.innerFormComponent)("submitShortcut", ctx_r0.submitShortcut)("contextGroups", i0.ɵɵpipeBind1(1, 6, ctx_r0.crudItemViewService.contextGroups$));
  }
}
function CrudEditorComponent_Conditional_8_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "ui-footer");
    i0.ɵɵelement(1, "crud-item-toolbar", 6);
    i0.ɵɵpipe(2, "async");
    i0.ɵɵpipe(3, "async");
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance();
    i0.ɵɵproperty("formService", i0.ɵɵpipeBind1(2, 3, ctx_r0.formService$))("item", i0.ɵɵpipeBind1(3, 5, ctx_r0.fullFormValue$))("config", ctx_r0.crudItemViewService.editorConfig);
  }
}
export let CrudEditorComponent = /*#__PURE__*/(() => {
  class CrudEditorComponent extends CrudItemViewComponent {
    constructor() {
      super(...arguments);
      this.destroyRef = inject(DestroyRef);
      this.innerFormComponent = null;
      this.showTitle = false; // disabled as we currently display the same title text in top bar already
      this.submitShortcut = this.crudItemViewService.editorConfig.submitShortcut;
      this.formService$ = new BehaviorSubject(null);
      // @Output() onSave:EventEmitter<any> = new EventEmitter();
      this.onAbort = new EventEmitter();
      this.fullFormValue$ = this.formService$.pipe(filter(fs => !!fs),
      // switch to initialized to make sure service is ready, then map back to pass the service itself
      switchMap(fs => {
        return fs.valueChanges$.pipe(startWith(null), map(() => fs.getValueIncludingReadonly()));
      }));
      this.title$ = this.crudItemViewService.modeAndItem$.pipe(map(({
        mode,
        item
      }) => {
        if (this.customTitle) return this.customTitle;
        if (mode === 'batch') return getItemModelText('batch', this.crudItemViewService.Model);
        if (mode === 'view') return getItemModelText('viewItem', item);
        if (mode === 'new') return getItemModelText('newItem', item);
        if (mode === 'edit') return getItemModelText('editItem', item);
        if (mode === 'clone') return getItemModelText('cloneItem', item);
        if (!mode) return "lade...";
        return "[Unbekannter Editor-Modus]";
      }), takeWhileAlive(this.destroyRef));
    }
    bindObservables() {
      super.bindObservables();
      const titleConfig = this.crudItemViewService.getPageTitle();
      if (titleConfig.setPageTitleForItemView) {
        this.title$.pipe(takeWhileAlive(this.destroyRef)).subscribe(title => {
          if (titleConfig.customPageTitle) {
            const modeAndItem = this.crudItemViewService.modeAndItem$.getValue();
            this.crudItemViewService.setPageTitle(titleConfig.customPageTitle(modeAndItem.mode, modeAndItem.item) || title);
          } else {
            this.crudItemViewService.setPageTitle(title);
          }
        });
      }
    }
    handleConfigurationUpdate(config) {
      const fs = config.formService;
      if (!IS_PRODUCTION) {
        if (config.mode === 'view') throw new Error('CrudEditor called with mode=view. This should never happen.');
        if (config.mode !== fs.getEditMode()) {
          console.log('invalid editor mode set in formService:', fs);
          throw new Error('CrudItemEditorDetails mode and FormService mode mismatch: ' + config.mode + ' ' + fs.getEditMode());
        }
      }
      // TODO: we need a state including instructions & innerFormComp.
      // right now only a change of the formService$ will trigger a full rerender.
      this.instructions = config.instructions;
      this.innerFormComponent = this.getInnerFormComponent(config.mode);
      this.cd.markForCheck();
      const oldFs = this.formService$.getValue();
      if (fs !== oldFs) {
        this.formService$.next(fs);
      } else {
        /**
         * TODO: #86c3e93x9
         * in 3.3.0 (https://github.com/yooniv3rsity/mainrepo/commit/d3ac2b7556585716ef152c1582d91c5d72fa0d22)
         * the below mechanism was introduced to force recreation of crud-fieldset.
         * Right now, most functionality seems to work fine without it, but we found some things that are messed up:
         * - Scene Editor media slots show inconstencies, displaying previous asset
         */
        this.formService$.next(null);
        setTimeout(() => {
          this.formService$.next(fs);
          this.cd.markForCheck();
        });
      }
    }
    getInnerFormComponent(mode) {
      const tmpl = this.crudItemViewService.getInnerFormTemplate();
      if (!tmpl) return null;
      if (isConstructor(tmpl)) {
        return tmpl;
      } else {
        return tmpl[mode];
      }
    }
    static {
      this.ɵfac = /*@__PURE__*/(() => {
        let ɵCrudEditorComponent_BaseFactory;
        return function CrudEditorComponent_Factory(__ngFactoryType__) {
          return (ɵCrudEditorComponent_BaseFactory || (ɵCrudEditorComponent_BaseFactory = i0.ɵɵgetInheritedFactory(CrudEditorComponent)))(__ngFactoryType__ || CrudEditorComponent);
        };
      })();
    }
    static {
      this.ɵcmp = /*@__PURE__*/i0.ɵɵdefineComponent({
        type: CrudEditorComponent,
        selectors: [["crud-editor"]],
        hostVars: 1,
        hostBindings: function CrudEditorComponent_HostBindings(rf, ctx) {
          if (rf & 2) {
            i0.ɵɵattribute("e2e-crud-editor", true);
          }
        },
        inputs: {
          showTitle: "showTitle",
          customTitle: "customTitle",
          submitShortcut: "submitShortcut",
          instructions: "instructions"
        },
        outputs: {
          onAbort: "onAbort"
        },
        features: [i0.ɵɵInheritDefinitionFeature],
        ngContentSelectors: _c1,
        decls: 9,
        vars: 9,
        consts: [[3, "fillHeight"], [3, "loading"], [3, "instructions", "crudItemViewService", "formService", "innerFormComponent", "submitShortcut", "contextGroups"], [1, "toolbar"], [1, "left"], [1, "center"], [3, "formService", "item", "config"]],
        template: function CrudEditorComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵprojectionDef(_c0);
            i0.ɵɵelementStart(0, "ui-panel", 0);
            i0.ɵɵconditionalCreate(1, CrudEditorComponent_Conditional_1_Template, 3, 3, "ui-header");
            i0.ɵɵprojection(2);
            i0.ɵɵelement(3, "ui-loading-indicator", 1);
            i0.ɵɵpipe(4, "async");
            i0.ɵɵconditionalCreate(5, CrudEditorComponent_Conditional_5_Template, 2, 8, "crud-fieldset", 2);
            i0.ɵɵpipe(6, "async");
            i0.ɵɵprojection(7, 1);
            i0.ɵɵconditionalCreate(8, CrudEditorComponent_Conditional_8_Template, 4, 7, "ui-footer");
            i0.ɵɵelementEnd();
          }
          if (rf & 2) {
            let tmp_3_0;
            i0.ɵɵproperty("fillHeight", true);
            i0.ɵɵadvance();
            i0.ɵɵconditional(ctx.showTitle ? 1 : -1);
            i0.ɵɵadvance(2);
            i0.ɵɵproperty("loading", !i0.ɵɵpipeBind1(4, 5, ctx.formService$));
            i0.ɵɵadvance(2);
            i0.ɵɵconditional((tmp_3_0 = i0.ɵɵpipeBind1(6, 7, ctx.formService$)) ? 5 : -1, tmp_3_0);
            i0.ɵɵadvance(3);
            i0.ɵɵconditional(ctx.crudItemViewService.editorConfig.showButtons ? 8 : -1);
          }
        },
        dependencies: [i1.UiPanelComponent, i1.UiHeaderComponent, i1.UiFooterComponent, UiLoadingIndicatorComponent, CrudFieldsetComponent, CrudItemToolbarComponent, AsyncPipe],
        encapsulation: 2
      });
    }
  }
  return CrudEditorComponent;
})();